import React, { useEffect, useState } from 'react';
import { Cascader } from 'ALIASED-antd';
import type { CascaderAutoProps } from 'ALIASED-antd';
import type { DefaultOptionType } from 'ALIASED-antd/lib/cascader';

type WrappedCascaderProps<
  OptionType extends DefaultOptionType = DefaultOptionType,
  ValueField extends keyof OptionType = keyof OptionType
> = CascaderAutoProps<OptionType, ValueField> & {
  'data-testid': string;
};

const useCascaderAccessibility = (container: HTMLElement | null) => {
  useEffect(() => {
    if (!container) return;

    const sanitize = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_');

    const syncCheckedState = () => {
      const checkboxItems = container.querySelectorAll<HTMLLIElement>('[role="menuitemcheckbox"]');
      checkboxItems.forEach((item) => {
        const isIndeterminate = !!item.querySelector('.ant-cascader-checkbox-indeterminate');
        if (isIndeterminate && item.getAttribute('aria-checked') !== 'mixed') {
          item.setAttribute('aria-checked', 'mixed');
        }
      });
    };

    const syncAccessibility = () => {
      const input = container.querySelector<HTMLInputElement>('input[role="combobox"]');
      if (!input?.id) return;

      const menus = container.querySelector<HTMLElement>('.ant-cascader-menus');
      if (menus) {
        if (!menus.id) menus.id = `${input.id}-listbox`;
        input.setAttribute('aria-owns', menus.id);
        input.setAttribute('aria-controls', menus.id);
      }

      syncCheckedState();

      const activeItems = container.querySelectorAll<HTMLLIElement>('.ant-cascader-menu-item-active');
      const activeItem = activeItems[activeItems.length - 1];
      if (!activeItem) {
        input.removeAttribute('aria-activedescendant');
        return;
      }

      if (!activeItem.id) {
        const pathKey = activeItem.getAttribute('data-path-key') ?? '';
        activeItem.id = `${menus?.id ?? input.id}-option-${sanitize(pathKey)}`;
      }
      if (input.getAttribute('aria-activedescendant') !== activeItem.id) {
        input.setAttribute('aria-activedescendant', activeItem.id);
      }
    };

    const observer = new MutationObserver(syncAccessibility);
    observer.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [container]);
};

export const WrappedAntdCascader = <
  OptionType extends DefaultOptionType = DefaultOptionType,
  ValueField extends keyof OptionType = keyof OptionType
>(
  props: WrappedCascaderProps<OptionType, ValueField>
) => {
  const { getPopupContainer, ...restProps } = props;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useCascaderAccessibility(container);

  return (
    <div ref={setContainer}>
      <Cascader
        {...restProps}
        getPopupContainer={
          getPopupContainer ?? ((node) => node.closest(`[data-testid="${props['data-testid']}"]`) || document.body)
        }
      />
    </div>
  );
};

WrappedAntdCascader.SHOW_CHILD = Cascader.SHOW_CHILD;
WrappedAntdCascader.SHOW_PARENT = Cascader.SHOW_PARENT;
WrappedAntdCascader.Panel = Cascader.Panel;
